import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import useSendGetRequest from '@Hooks/useSendGetRequest';
import { EXEMPTION_REQUEST_PDF } from '@API/APIRoutes';

/** Fetches the exemption request PDF as a blob, previews it in an iframe and offers a download. */
export const ExemptionPdfDialog = ({ open, onClose, planId }) => {
  const { loading, getData: getPdf } = useSendGetRequest(true, true);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      try {
        const response = await getPdf(`${EXEMPTION_REQUEST_PDF}/${planId}`);
        if (!response) return;
        const blob = response instanceof Blob ? response : new Blob([response], { type: 'application/pdf' });
        objectUrl = window.URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (error) {
        console.error('Failed to load exemption request PDF:', error);
      }
    };
    if (open && planId) load();
    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      setPdfUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planId]);

  const download = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', `exemption_request_${planId}.pdf`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Exemption Request PDF
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '100%' }}>
        {pdfUrl ? (
          <iframe src={pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Exemption Request PDF" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
            <CircularProgress size={60} />
            <h3 style={{ margin: 0, color: '#666' }}>{loading ? 'Generating PDF...' : 'Preparing preview...'}</h3>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.300', p: 2 }}>
        <Button onClick={download} variant="contained" startIcon={<DownloadIcon />} disabled={!pdfUrl || loading}>
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExemptionPdfDialog;
